import React, { useState, useEffect, useCallback } from 'react';
import supabase from '../../supabase.js';
import AdminBar from './adminBar.jsx';
import { Helmet } from 'react-helmet-async';

export default function BusinessWritten() {
    const [currentReview, setCurrentReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [requestsLeft, setRequestsLeft] = useState(0); 
    const [isActionLoading, setIsActionLoading] = useState(false); 
    const [signedUrl, setSignedUrl] = useState('');
    const [pendingAction, setPendingAction] = useState(null); 
    const [reasonText, setReasonText] = useState('');


    const PossibleReasons = {
        "ai_flag": "The review was flagged by our AI system for potential manipulation or fraudulent content.",
        "content_missmatch": "The screenshot didn't match the review text, indicating possible tampering.",
        "review_Star_Tone_missmatch": "The sentiment of the review text didn't align with the star rating, suggesting inauthenticity.",
        "Low_quality_screenshot": "The screenshot is of low quality, making it difficult to verify the review.",
    }
    // FIX 1: Explicitly ignore deleted rows in the count
    const fetchPendingCount = useCallback(async () => {
        const { count } = await supabase
            .from('reviews')
            .select('*', { count: 'exact', head: true })
            .eq('is_approved', false)
            .eq('is_deleted', false) // <--- CRITICAL
            .eq('submission_type', 'bwt');
        setRequestsLeft(count || 0);
    }, []);

    // FIX 2: Only fetch reviews that are NOT approved AND NOT deleted
    const fetchNextPendingReview = useCallback(async () => {
        setLoading(true); 
        setErrorMsg(''); 
        setSignedUrl(''); 
        setPendingAction(null);
        
        try {
            const { data, error } = await supabase
                .from('reviews')
                .select('*')
                .eq('is_approved', false)
                .eq('is_deleted', false) // <--- THIS PREVENTS RE-FETCHING DECLINED ONES
                .eq('submission_type', 'bwt')
                .order('id', { ascending: true })
                .limit(1);

            if (error) throw error;

            if (data?.[0]) {
                setCurrentReview(data[0]);
                const { data: sData } = await supabase.storage.from('review-proof').createSignedUrl(data[0].screenshot_url, 600);
                setSignedUrl(sData?.signedUrl);
            } else {
                setCurrentReview(null);
                setErrorMsg("No pending reviews.");
            }
        } catch (err) { 
            setErrorMsg(err.message); 
        } finally { 
            setLoading(false); 
        }
    }, []);

   const handleAction = async () => {
        if (!currentReview || !pendingAction || isActionLoading) return;
        setIsActionLoading(true);

        try {
            const isApprove = pendingAction === 'approve';
            const targetCompany = currentReview.company;
            const evidencePath = currentReview.screenshot_url;
            
            // 1. UPDATE THE REVIEW STATUS IN DATABASE
            const { error: reviewError } = await supabase
                .from('reviews')
                .update({ 
                    is_approved: isApprove, 
                    status: isApprove ? 'verified' : 'declined',
                    is_deleted: !isApprove, // TRUE if declined (marks as trash)
                    Decline_Reason: isApprove ? null : reasonText
                })
                .eq('id', currentReview.id);

            if (reviewError) throw reviewError;

            // 2. PRIVACY PURGE: Delete the proof image from the bucket
            if (evidencePath) {
                const { error: storageError } = await supabase
                    .storage
                    .from('review-proof')
                    .remove([evidencePath]);

                if (storageError) {
                    // We log this but don't stop the flow; the DB record is already updated
                    console.error("Privacy Cleanup Failed:", storageError.message);
                } else {
                    console.log("Forensic evidence purged for privacy.");
                }
            }

            // 3. RECALCULATE TRUSTSCORE (Only if approved)
            if (isApprove) {
                const { data: allReviews, error: fetchError } = await supabase
                    .from('reviews')
                    .select('stars')
                    .eq('company', targetCompany)
                    .eq('is_approved', true)
                    .eq('is_deleted', false);

                if (fetchError) throw fetchError;

                let newTrustScore = 0;
                let totalReviews = 0;

                if (allReviews && allReviews.length > 0) {
                    totalReviews = allReviews.length;
                    const sumStars = allReviews.reduce((acc, rev) => acc + rev.stars, 0);
                    newTrustScore = parseFloat((sumStars / totalReviews).toFixed(1));
                }

                const { error: bizError } = await supabase
                    .from('businesses')
                    .update({ 
                        trustscore: newTrustScore, 
                        reviews: totalReviews 
                    })
                    .eq('www', targetCompany);

                if (bizError) throw bizError;
            }

            // 4. UI CLEANUP & LOAD NEXT TASK
            setPendingAction(null); 
            await fetchPendingCount(); 
            await fetchNextPendingReview(); 
            
        } catch (err) { 
            console.error("Audit Action Failed:", err.message);
            alert("Action Failed: " + err.message); 
        } finally { 
            setIsActionLoading(false); 
            setReasonText('');
        }
    };
    
    useEffect(() => { 
        fetchPendingCount(); 
        fetchNextPendingReview(); 
    }, [fetchPendingCount, fetchNextPendingReview]);

    if (loading) return <div className='p-20 text-center font-black animate-pulse text-white bg-[#0f172a] min-h-screen uppercase tracking-widest'>Analyzing Forensic Data...</div>;

    return (
        <div className='w-full min-h-screen bg-[#0f172a] flex flex-col items-center pb-20 font-poppins text-white'>
                   <Helmet>
                    <title>Admin | Sitemanifest</title> 
                    
                    <meta name="robots" content="noindex, nofollow" />
                    
                    <meta name="googlebot" content="noarchive" />
                  </Helmet>
            <AdminBar />
            <div className='pt-24 pb-10 text-center'>
                <h1 className='text-5xl font-black tracking-tighter uppercase italic'>BWT Audit Terminal</h1>
                <p className='text-blue-400 font-bold mt-2 tracking-widest'>{requestsLeft} PENDING VERIFICATIONS</p>
            </div>

            {!currentReview ? (
                <div className='bg-slate-800 p-12 rounded-[3rem] text-center border border-slate-700 shadow-2xl'>
                    <p className='text-slate-400 font-bold mb-6'>{errorMsg || "Vault Clear. All reviews processed."}</p>
                    <button onClick={() => fetchNextPendingReview()} className='bg-blue-600 hover:bg-blue-500 text-white px-10 py-4 rounded-2xl font-black transition-colors'>RE-SCAN DATABASE</button>
                </div>
            ) : (
                <div className='flex flex-col lg:flex-row gap-10 px-10 max-w-7xl w-full'>
                    {/* LEFT: EVIDENCE */}
                    <div className='flex-1 flex flex-col gap-6'>
                        <div className={`aspect-[4/3] bg-black rounded-[3rem] flex items-center justify-center overflow-hidden shadow-2xl border-[12px] transition-all duration-500 ${currentReview.is_flagged ? 'border-red-600 shadow-red-900/40' : 'border-slate-800'}`}>
                            <img 
                                src={signedUrl} 
                                alt="Evidence" 
                                className='w-full h-full object-contain cursor-zoom-in' 
                                onClick={() => window.open(signedUrl, '_blank')} 
                            />
                        </div>
                        <div className='flex flex-col gap-4'>
                            <button 
                                onClick={() => window.open(`https://lens.google.com/uploadbyurl?url=${encodeURIComponent(signedUrl)}`, '_blank')} 
                                className="w-full py-3 bg-slate-800/50 border border-slate-700 rounded-2xl text-[10px] font-black tracking-widest hover:bg-blue-900 transition-colors uppercase"
                            >
                                🔍 Cross-Reference Source (Google Lens)
                            </button>
                            <div className='flex gap-4'>
                                <button 
                                    onClick={() => setPendingAction('decline')} 
                                    className={`flex-1 py-5 rounded-3xl font-black transition-all ${pendingAction === 'decline' ? 'bg-red-600 ring-8 ring-red-900/50 scale-95' : 'bg-slate-800 border border-slate-700 opacity-50 hover:opacity-80'}`}
                                >
                                    DECLINE
                                </button>
                                <button 
                                    onClick={() => setPendingAction('approve')} 
                                    className={`flex-1 py-5 rounded-3xl font-black transition-all ${pendingAction === 'approve' ? 'bg-green-600 ring-8 ring-green-900/50 scale-95' : 'bg-slate-800 border border-slate-700 opacity-50 hover:opacity-80'}`}
                                >
                                    APPROVE
                                </button>
                            </div>
                            <button 
                                onClick={handleAction} 
                                disabled={
        !pendingAction || 
        isActionLoading || 
        (pendingAction === 'decline' && !reasonText)
    }
                                className='w-full py-8 bg-white text-black font-black text-2xl rounded-[2rem] shadow-2xl disabled:opacity-20 hover:bg-slate-200 transition-all uppercase'
                            >
                                {isActionLoading ? 'Synchronizing...' : 'Commit Decision'}
                            </button>
                        </div>
                    </div>

                    

                    {/* REASON SELECTION PANEL */}

                    {/* RIGHT: INTELLIGENCE */}
                    <div className='w-full lg:w-[450px] flex flex-col gap-6'>
                        <div className={`p-8 rounded-[3rem] shadow-2xl transition-all duration-700 ${currentReview.is_flagged ? 'bg-red-600' : 'bg-slate-800 border border-slate-700'}`}>
                            <div className='flex justify-between items-center mb-6'>
                                <h3 className='font-black text-xs uppercase tracking-[0.2em]'>AI Forensic Score</h3>
                                {currentReview.is_flagged && <span className='bg-white text-red-600 text-[10px] px-2 py-1 rounded-full font-black animate-pulse'>HIGH RISK</span>}
                            </div>
                            <p className='text-3xl font-black leading-tight italic uppercase'>
                                {currentReview.is_flagged ? currentReview.flag_reason : "Low Risk Profile"}
                            </p>
                            <div className='mt-6 h-4 bg-black/20 rounded-full overflow-hidden p-1'>
                                <div 
                                    className='h-full bg-white rounded-full transition-all duration-1000' 
                                    style={{width: `${(currentReview.ai_score * 100).toFixed(0)}%`}} 
                                />
                            </div>
                            <p className='text-[10px] font-black mt-2 text-right uppercase tracking-widest'>AI Certainty: {(currentReview.ai_score * 100).toFixed(1)}%</p>
                        </div>

                        <div className='bg-slate-800 border border-slate-700 p-8 rounded-[3rem] shadow-2xl'>
                            <h2 className='text-blue-400 font-black text-xs uppercase tracking-[0.2em] mb-6'>Submission Data</h2>
                            <div className='flex flex-col gap-5'>
                                <DetailItem label="Business Key" value={currentReview.company} />
                                <DetailItem label="Author Identity" value={currentReview.author} />
                                <DetailItem label="IP Geolocation" value={currentReview.countr} />
                                <div className='mt-2'>
                                    <p className='text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2'>Review Content</p>
                                    <div className='p-4 bg-slate-900 rounded-2xl border border-slate-700 text-xs italic text-slate-300 shadow-inner'>
                                        "{currentReview.text}"
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {pendingAction === 'decline' && (
    <div className="mt-4 flex flex-col gap-2 animate-in fade-in slide-in-from-top-4 duration-300">
        <p className="text-[10px] font-black text-red-500 uppercase tracking-[0.2em] mb-2 ml-2">
            Select Rejection Reason:
        </p>
        <div className="grid grid-cols-1 gap-2">
            {Object.entries(PossibleReasons).map(([key, text]) => (
                <button
                    key={key}
                    onClick={() => setReasonText(text)}
                    className={`text-left p-4 rounded-2xl text-[11px] font-bold border transition-all ${
                        reasonText === text 
                        ? 'bg-red-600 border-red-500 text-white shadow-lg' 
                        : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500'
                    }`}
                >
                    {text}
                </button>
            ))}
        </div>
    </div>
)}

                </div>

                
            )}
            
        </div>
    );
}

function DetailItem({ label, value }) {
    return (
        <div className='border-b border-slate-700/50 pb-2 last:border-0'>
            <p className='text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1'>{label}</p>
            <p className='font-bold text-md text-white uppercase truncate'>{value || 'UNKNOWN_ID'}</p>
        </div>
    );
}